/**
 * ProductReviewsPage - Admin page for reviewing flagged product nutrition data
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, CheckCircle, XCircle, Clock, Edit, 
  RefreshCw, ChevronRight, Bot, User, Package, Trash2
} from 'lucide-react';
import axios from 'axios';
import { resolveNutritionImageUrl } from '@/utils/imageUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductReviewsPage = () => {
  const [activeTab, setActiveTab] = useState('reviews');
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [productEditForm, setProductEditForm] = useState({
    name: '',
    brand: '',
    default_portion_g: 100,
    calories_per_100g: 0,
    protein_per_100g: 0,
    carbs_per_100g: 0,
    fat_per_100g: 0,
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [adminPhotoTarget, setAdminPhotoTarget] = useState(null);
  const [uploadingAdminPhoto, setUploadingAdminPhoto] = useState(false);
  const [autoRecoveringImages, setAutoRecoveringImages] = useState(false);
  const [autoRecoveryReport, setAutoRecoveryReport] = useState(null);
  const adminPhotoInputRef = useRef(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, corrected: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedNutrition, setEditedNutrition] = useState({});
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (activeTab === 'reviews') {
      loadReviews();
      loadStats();
      return;
    }
    loadProducts();
  }, [activeTab, statusFilter]);

  useEffect(() => {
    if (activeTab !== 'products') return;
    const timer = setTimeout(() => {
      loadProducts();
    }, 250);
    return () => clearTimeout(timer);
  }, [productSearch, activeTab]);

  const loadReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/nutrition/admin/product-reviews`, {
        params: { status: statusFilter },
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Load reviews error:', error);
      toast.error('Fehler beim Laden der Reviews');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/nutrition/admin/product-reviews/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Load stats error:', error);
    }
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/nutrition/admin/products`, {
        params: { search: productSearch || undefined, limit: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data?.items || []);
    } catch (error) {
      console.error('Load products error:', error);
      toast.error('Produkte konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openProductEdit = (product) => {
    setEditingProduct(product);
    setProductEditForm({
      name: product.name || '',
      brand: product.brand || '',
      default_portion_g: Number(product.default_portion_g || product.serving_size_g || 100),
      calories_per_100g: Number(product.calories_per_100g || 0),
      protein_per_100g: Number(product.protein_per_100g || 0),
      carbs_per_100g: Number(product.carbs_per_100g || 0),
      fat_per_100g: Number(product.fat_per_100g || 0),
    });
  };

  const saveProductEdit = async () => {
    if (!editingProduct?.id) return;
    try {
      setSavingProduct(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/portal/nutrition/admin/custom-products/${editingProduct.id}`,
        {
          name: productEditForm.name,
          brand: productEditForm.brand,
          default_portion_g: Number(productEditForm.default_portion_g || 100),
          calories_per_100g: Number(productEditForm.calories_per_100g || 0),
          protein_per_100g: Number(productEditForm.protein_per_100g || 0),
          carbs_per_100g: Number(productEditForm.carbs_per_100g || 0),
          fat_per_100g: Number(productEditForm.fat_per_100g || 0),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Produkt aktualisiert');
      setEditingProduct(null);
      loadProducts();
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Produkt konnte nicht aktualisiert werden');
    } finally {
      setSavingProduct(false);
    }
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const openAdminPhotoUpload = (product) => {
    setAdminPhotoTarget(product);
    adminPhotoInputRef.current?.click();
  };

  const handleAdminPhotoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !adminPhotoTarget?.id) return;

    try {
      setUploadingAdminPhoto(true);
      const token = localStorage.getItem('token');
      const frontImage = await toBase64(file);

      await axios.post(
        `${API}/portal/nutrition/admin/custom-products/${encodeURIComponent(adminPhotoTarget.id)}/upload-photo`,
        { front_image: frontImage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Produktfoto wurde aktualisiert');
      await loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Foto konnte nicht aktualisiert werden');
    } finally {
      setUploadingAdminPhoto(false);
      setAdminPhotoTarget(null);
    }
  };

  const runAutoImageRecovery = async () => {
    try {
      setAutoRecoveringImages(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/portal/nutrition/admin/products/auto-recover-images`,
        {
          limit: 12,
          search: productSearch || null,
          only_missing: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setAutoRecoveryReport(response.data || null);

      const summary = response.data?.summary || {};
      const timeoutHint = response.data?.timed_out ? ' (Teil-Ergebnis wegen Zeitlimit)' : '';
      toast.success(`Auto-Recovery fertig: ${summary.recovered || 0} repariert, ${summary.failed || 0} fehlgeschlagen${timeoutHint}`);
      await loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Auto-Recovery fehlgeschlagen');
    } finally {
      setAutoRecoveringImages(false);
    }
  };

  const handleAction = async (reviewId, action, correctedData = null) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/nutrition/admin/product-reviews/${reviewId}`, {
        action,
        corrected_nutrition: correctedData?.nutrition_per_100g,
        corrected_micronutrients: correctedData?.micronutrients_per_100g,
        admin_notes: adminNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Review wurde ${action === 'approve' ? 'genehmigt' : action === 'correct' ? 'korrigiert' : 'abgelehnt'}`);
      setShowEditDialog(false);
      setSelectedReview(null);
      setAdminNotes('');
      loadReviews();
      loadStats();
    } catch (error) {
      toast.error('Fehler beim Verarbeiten');
    }
  };

  const handleDeleteProduct = async (item) => {
    const productRef = item.product_id || item.id || item.custom_product_id || item.product_barcode || item.barcode;
    if (!productRef) {
      toast.error('Produkt-ID fehlt');
      return;
    }

    const confirmed = window.confirm(`Produkt "${item.product_name || item.name || 'Unbekannt'}" wirklich löschen?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/portal/nutrition/admin/custom-products/${encodeURIComponent(productRef)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produkt gelöscht');
      loadReviews();
      loadStats();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const openEditDialog = (review) => {
    setSelectedReview(review);
    setEditedNutrition({
      nutrition_per_100g: { ...review.product_data?.nutrition_per_100g },
      micronutrients_per_100g: { ...review.product_data?.micronutrients_per_100g }
    });
    setAdminNotes('');
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-600"><Clock size={12} className="mr-1" />Ausstehend</Badge>;
      case 'approved':
      case 'auto_approved':
        return <Badge className="bg-green-500/20 text-green-600"><CheckCircle size={12} className="mr-1" />{status === 'auto_approved' ? 'Auto-Genehmigt' : 'Genehmigt'}</Badge>;
      case 'corrected':
        return <Badge className="bg-blue-500/20 text-blue-600"><Edit size={12} className="mr-1" />Korrigiert</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-600"><XCircle size={12} className="mr-1" />Abgelehnt</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getAIRecommendationBadge = (validation) => {
    if (!validation) return null;
    const rec = validation.recommendation;
    if (rec === 'approve') return <Badge className="bg-green-500/20 text-green-600"><Bot size={12} className="mr-1" />KI: OK</Badge>;
    if (rec === 'needs_review') return <Badge className="bg-yellow-500/20 text-yellow-600"><Bot size={12} className="mr-1" />KI: Prüfen</Badge>;
    if (rec === 'reject') return <Badge className="bg-red-500/20 text-red-600"><Bot size={12} className="mr-1" />KI: Probleme</Badge>;
    return null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <input
        ref={adminPhotoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAdminPhotoChange}
        data-testid="admin-products-photo-upload-input"
      />

      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Package className="text-orange-500" />
          Nährwert-Reviews
        </h1>
        <p className="text-gray-600">Prüfen und korrigieren Sie gemeldete Produktdaten</p>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="product-reviews-page-tabs">
        <Button
          type="button"
          variant={activeTab === 'reviews' ? 'default' : 'outline'}
          onClick={() => setActiveTab('reviews')}
          data-testid="product-reviews-tab-button"
        >
          Meldungen
        </Button>
        <Button
          type="button"
          variant={activeTab === 'products' ? 'default' : 'outline'}
          onClick={() => setActiveTab('products')}
          data-testid="all-products-tab-button"
        >
          Alle Produkte
        </Button>
      </div>

      {activeTab === 'reviews' && (
      <>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ausstehend</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="text-yellow-500" size={24} />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'approved' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setStatusFilter('approved')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Genehmigt</p>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
              </div>
              <CheckCircle className="text-green-500" size={24} />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'corrected' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter('corrected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Korrigiert</p>
                <p className="text-2xl font-bold text-blue-600">{stats.corrected}</p>
              </div>
              <Edit className="text-blue-500" size={24} />
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${statusFilter === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Abgelehnt</p>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="text-red-500" size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reviews List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>
            {statusFilter === 'pending' ? 'Ausstehende Reviews' : 
             statusFilter === 'approved' ? 'Genehmigte Reviews' :
             statusFilter === 'corrected' ? 'Korrigierte Reviews' :
             statusFilter === 'rejected' ? 'Abgelehnte Reviews' : 'Alle Reviews'}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={loadReviews}>
            <RefreshCw size={14} className="mr-2" />
            Aktualisieren
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="animate-spin text-gray-400" size={32} />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package size={48} className="mx-auto mb-2 opacity-30" />
              <p>Keine Reviews in dieser Kategorie</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div 
                  key={review.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">{review.product_name || 'Unbekanntes Produkt'}</h3>
                        {getStatusBadge(review.status)}
                        {getAIRecommendationBadge(review.ai_validation)}
                      </div>
                      
                      <div className="text-sm text-gray-500 space-y-1">
                        <p><span className="font-medium">Barcode:</span> {review.product_barcode || 'N/A'}</p>
                        <p><span className="font-medium">Gemeldet von:</span> {review.flagged_by_name || 'Kunde'}</p>
                        <p><span className="font-medium">Datum:</span> {new Date(review.flagged_at).toLocaleString('de-CH')}</p>
                        <p><span className="font-medium">Grund:</span> {review.reason === 'incorrect_nutrition' ? 'Falsche Nährwerte' : review.reason === 'portion_incorrect' ? 'Portion falsch' : review.reason}</p>
                        {review.comment && <p><span className="font-medium">Kommentar:</span> {review.comment}</p>}
                      </div>

                      {/* AI Validation Details */}
                      {review.ai_validation && (
                        <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                          <p className="text-sm font-medium flex items-center gap-1 mb-2">
                            <Bot size={14} />
                            KI-Analyse
                          </p>
                          {review.ai_validation.issues?.length > 0 && (
                            <div className="space-y-1">
                              {review.ai_validation.issues.map((issue, idx) => (
                                <p key={idx} className="text-sm text-orange-600">
                                  • {issue.field}: {issue.issue} 
                                  {issue.severity && <span className={`ml-1 text-xs ${issue.severity === 'high' ? 'text-red-600' : 'text-yellow-600'}`}>({issue.severity})</span>}
                                </p>
                              ))}
                            </div>
                          )}
                          {review.ai_validation.notes && (
                            <p className="text-sm text-gray-600 mt-1">{review.ai_validation.notes}</p>
                          )}
                          {review.ai_validation.confidence && (
                            <p className="text-xs text-gray-500 mt-1">
                              Konfidenz: {Math.round(review.ai_validation.confidence * 100)}%
                            </p>
                          )}
                        </div>
                      )}

                      {/* Current Nutrition Data */}
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div className="bg-orange-50 p-2 rounded">
                          <p className="text-orange-800 font-medium">Kalorien</p>
                          <p>{review.product_data?.nutrition_per_100g?.calories || '?'} kcal</p>
                        </div>
                        <div className="bg-red-50 p-2 rounded">
                          <p className="text-red-800 font-medium">Protein</p>
                          <p>{review.product_data?.nutrition_per_100g?.protein || '?'} g</p>
                        </div>
                        <div className="bg-yellow-50 p-2 rounded">
                          <p className="text-yellow-800 font-medium">Kohlenhydrate</p>
                          <p>{review.product_data?.nutrition_per_100g?.carbs || '?'} g</p>
                        </div>
                        <div className="bg-blue-50 p-2 rounded">
                          <p className="text-blue-800 font-medium">Fett</p>
                          <p>{review.product_data?.nutrition_per_100g?.fat || '?'} g</p>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      {review.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-500 hover:bg-green-600"
                            onClick={() => handleAction(review.id, 'approve')}
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Genehmigen
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openEditDialog(review)}
                          >
                            <Edit size={14} className="mr-1" />
                            Korrigieren
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleAction(review.id, 'reject')}
                          >
                            <XCircle size={14} className="mr-1" />
                            Ablehnen
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-700 border-red-400 hover:bg-red-100"
                        onClick={() => handleDeleteProduct(review)}
                        data-testid={`product-review-delete-product-${review.id}`}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Produkt löschen
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      </>
      )}

      {activeTab === 'products' && (
      <Card data-testid="all-products-card">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <CardTitle>Alle Produkte in DB</CardTitle>
          <div className="flex items-center gap-2">
            <Input
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Suche Name, Marke, Barcode"
              className="w-72"
              data-testid="all-products-search-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={runAutoImageRecovery}
              disabled={autoRecoveringImages}
              data-testid="all-products-auto-recover-button"
            >
              <RefreshCw size={14} className={`mr-2 ${autoRecoveringImages ? 'animate-spin' : ''}`} />
              Fehlende Bilder automatisch reparieren
            </Button>
            <Button variant="outline" size="sm" onClick={loadProducts} data-testid="all-products-refresh-button">
              <RefreshCw size={14} className="mr-2" />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {autoRecoveryReport?.summary && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="all-products-auto-recover-summary">
              <p className="text-sm font-semibold text-amber-900">Auto-Recovery Ergebnis</p>
              <p className="text-xs text-amber-800 mt-1">
                Verarbeitet: {autoRecoveryReport.summary.processed || 0} · Repariert: {autoRecoveryReport.summary.recovered || 0} · Fehlgeschlagen: {autoRecoveryReport.summary.failed || 0}
              </p>
              {autoRecoveryReport.timed_out && (
                <p className="text-xs text-amber-700 mt-1" data-testid="all-products-auto-recover-timeout-note">
                  Lauf wurde wegen Zeitlimit gestoppt. Button erneut klicken für nächsten Batch.
                </p>
              )}
              {!autoRecoveryReport.serpapi_configured && (
                <p className="text-xs text-red-700 mt-1" data-testid="all-products-auto-recover-serpapi-warning">
                  SerpAPI ist nicht konfiguriert. Bitte API-Key unter Einstellungen → API & KI eintragen.
                </p>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-gray-400" size={32} /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">Keine Produkte gefunden</div>
          ) : (
            <div className="space-y-3" data-testid="all-products-list">
              {products.map((product) => (
                <div key={product.id} className="border rounded-lg p-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                  <div className="flex-1 min-w-0 flex items-center gap-3">
                    {resolveNutritionImageUrl(product.image_url || product.front_image_url) ? (
                      <img
                        src={resolveNutritionImageUrl(product.image_url || product.front_image_url)}
                        alt="Produkt"
                        className="w-12 h-12 rounded object-cover border border-gray-200"
                        data-testid={`all-products-image-${product.id}`}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-500" data-testid={`all-products-image-fallback-${product.id}`}>
                        📦
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{product.name || 'Unbenanntes Produkt'}</p>
                      <p className="text-sm text-gray-500 truncate">{product.brand || 'Ohne Marke'} • {product.barcode || 'kein Barcode'}</p>
                      <p className="text-xs text-gray-500">Portion: {Math.round(Number(product.default_portion_g || product.serving_size_g || 0)) || 0}g</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs" data-testid={`all-products-image-status-${product.id}`}>
                          Bildstatus: {product.image_storage_status || 'unbekannt'}
                        </Badge>
                        <span className="text-[11px] text-gray-500 break-all" data-testid={`all-products-image-ref-${product.id}`}>
                          {product.image_storage_path || product.image_url || 'kein Bildpfad'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openAdminPhotoUpload(product)}
                      disabled={uploadingAdminPhoto}
                      data-testid={`all-products-upload-photo-${product.id}`}
                    >
                      Foto ändern
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openProductEdit(product)} data-testid={`all-products-edit-${product.id}`}>
                      <Edit size={14} className="mr-1" />
                      Bearbeiten
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-700 border-red-400 hover:bg-red-100" onClick={() => handleDeleteProduct(product)} data-testid={`all-products-delete-${product.id}`}>
                      <Trash2 size={14} className="mr-1" />
                      Löschen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Product Edit Dialog */}
      <Dialog open={Boolean(editingProduct)} onOpenChange={(open) => !open && setEditingProduct(null)}>
        <DialogContent className="max-w-xl" data-testid="all-products-edit-dialog">
          <DialogHeader>
            <DialogTitle>Produkt bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={productEditForm.name} onChange={(e) => setProductEditForm((prev) => ({ ...prev, name: e.target.value }))} data-testid="all-products-edit-name-input" />
            </div>
            <div>
              <Label>Marke</Label>
              <Input value={productEditForm.brand} onChange={(e) => setProductEditForm((prev) => ({ ...prev, brand: e.target.value }))} data-testid="all-products-edit-brand-input" />
            </div>
            <div>
              <Label>Portion (g)</Label>
              <Input type="number" value={productEditForm.default_portion_g} onChange={(e) => setProductEditForm((prev) => ({ ...prev, default_portion_g: e.target.value }))} data-testid="all-products-edit-portion-input" />
            </div>
            <div>
              <Label>kcal / 100g</Label>
              <Input type="number" value={productEditForm.calories_per_100g} onChange={(e) => setProductEditForm((prev) => ({ ...prev, calories_per_100g: e.target.value }))} data-testid="all-products-edit-calories-input" />
            </div>
            <div>
              <Label>Protein / 100g</Label>
              <Input type="number" value={productEditForm.protein_per_100g} onChange={(e) => setProductEditForm((prev) => ({ ...prev, protein_per_100g: e.target.value }))} data-testid="all-products-edit-protein-input" />
            </div>
            <div>
              <Label>KH / 100g</Label>
              <Input type="number" value={productEditForm.carbs_per_100g} onChange={(e) => setProductEditForm((prev) => ({ ...prev, carbs_per_100g: e.target.value }))} data-testid="all-products-edit-carbs-input" />
            </div>
            <div>
              <Label>Fett / 100g</Label>
              <Input type="number" value={productEditForm.fat_per_100g} onChange={(e) => setProductEditForm((prev) => ({ ...prev, fat_per_100g: e.target.value }))} data-testid="all-products-edit-fat-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProduct(null)} data-testid="all-products-edit-cancel-button">Abbrechen</Button>
            <Button onClick={saveProductEdit} disabled={savingProduct || !productEditForm.name?.trim()} data-testid="all-products-edit-save-button">{savingProduct ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit/Correct Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nährwerte korrigieren</DialogTitle>
          </DialogHeader>
          
          {selectedReview && (
            <div className="space-y-4">
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="font-medium">{selectedReview.product_name}</p>
                <p className="text-sm text-gray-500">Barcode: {selectedReview.product_barcode}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kalorien (kcal)</Label>
                  <Input 
                    type="number"
                    value={editedNutrition.nutrition_per_100g?.calories || ''}
                    onChange={(e) => setEditedNutrition({
                      ...editedNutrition,
                      nutrition_per_100g: {
                        ...editedNutrition.nutrition_per_100g,
                        calories: parseFloat(e.target.value) || 0
                      }
                    })}
                  />
                </div>
                <div>
                  <Label>Protein (g)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={editedNutrition.nutrition_per_100g?.protein || ''}
                    onChange={(e) => setEditedNutrition({
                      ...editedNutrition,
                      nutrition_per_100g: {
                        ...editedNutrition.nutrition_per_100g,
                        protein: parseFloat(e.target.value) || 0
                      }
                    })}
                  />
                </div>
                <div>
                  <Label>Kohlenhydrate (g)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={editedNutrition.nutrition_per_100g?.carbs || ''}
                    onChange={(e) => setEditedNutrition({
                      ...editedNutrition,
                      nutrition_per_100g: {
                        ...editedNutrition.nutrition_per_100g,
                        carbs: parseFloat(e.target.value) || 0
                      }
                    })}
                  />
                </div>
                <div>
                  <Label>Fett (g)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    value={editedNutrition.nutrition_per_100g?.fat || ''}
                    onChange={(e) => setEditedNutrition({
                      ...editedNutrition,
                      nutrition_per_100g: {
                        ...editedNutrition.nutrition_per_100g,
                        fat: parseFloat(e.target.value) || 0
                      }
                    })}
                  />
                </div>
              </div>

              <div>
                <Label>Admin-Notiz</Label>
                <Textarea 
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Optionale Notiz zur Korrektur..."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-blue-500 hover:bg-blue-600"
              onClick={() => handleAction(selectedReview.id, 'correct', editedNutrition)}
            >
              Speichern & Korrigieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductReviewsPage;
